import { useMemo } from 'react';
import { useOtherInfoContext } from '../OtherInfoContext';
import { resolveFieldValue } from '../useFieldState';
import { ImageSelector } from '../../ImageSelector';
import { FieldWrapper } from './FieldWrapper';
import { allowsImageAndVideo } from '@moko/utils';
import type { FieldComponentProps } from './types';

export const ImageField = ({
  field,
  fieldValue,
  isFieldDisabled,
  validationMessage,
}: FieldComponentProps) => {
  const {
    params,
    otherInfoObj,
    handleOtherInfoChange,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
  } = useOtherInfoContext();

  const imageLocation = useMemo(() => {
    const locationRaw = resolveFieldValue(
      `${field.name}_Location`,
      otherInfoObj,
      params.metadata.fields,
      getRepeatingPanelIndex,
      currentElementInEntityRepeatingPanel
    );
    if (!locationRaw) return null;
    if (typeof locationRaw === 'string') {
      try {
        return JSON.parse(locationRaw);
      } catch {
        return null;
      }
    }
    return locationRaw;
  }, [
    field.name,
    otherInfoObj,
    params.metadata.fields,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
  ]);

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      <ImageSelector
        value={fieldValue ?? ''}
        purpose="OtherInfo"
        imageSource={fieldValue ?? ''}
        imageLocation={imageLocation}
        uploadImage={true}
        onUpload={(
          _id: number,
          url: string,
          _name: string,
          location?: { latitude: number; longitude: number }
        ) => {
          handleOtherInfoChange(field.name, url);
          if (location) {
            handleOtherInfoChange(`${field.name}_Location`, location);
          }
        }}
        imageSelectionAllowed={false}
        allowVideo={allowsImageAndVideo(field.options)}
        disabled={isFieldDisabled}
      />
    </FieldWrapper>
  );
};
